import { ChevronRight } from 'lucide-react'

import { type IssueSeverity } from '@/shared/constants/issue-status'

import { COL_LOCATION, COL_STATUS, ISSUE_TABLE_COLS } from './issue-table-grid'
import { ageDays } from '../../../lib/issues'
import { prettyName } from '../../../lib/mandal-name'

import type { GisIssue } from '../../../api/types'
import type { ResolutionTargets } from '../../../lib/resolution-targets'

export const SEV_LETTER: Record<IssueSeverity, string> = { critical: 'C', non_critical: 'N' }
export const SEV_COLOR: Record<IssueSeverity, string> = { critical: '#dc2626', non_critical: '#ea580c' }

type StatusLabel = 'Open' | 'Pending' | 'Completed'

export const shortStatus = (i: GisIssue): StatusLabel =>
  i.status === 'completed' ? 'Completed'
  : i.status === 'pending' ? 'Pending'
  : 'Open'

/** Status-chip tints — same semantic hues as the StatusTiles palette (open red / completed green),
 *  amber for the Pending (acknowledged, ETA set) sub-state the merged "Open" view would otherwise hide. */
const STATUS_CHIP: Record<StatusLabel, { bg: string; text: string }> = {
  'Open': { bg: 'var(--gis-red-tint)', text: '#dc2626' },
  'Pending': { bg: 'var(--gis-amber-tint)', text: '#b45309' },
  'Completed': { bg: 'var(--gis-green-tint)', text: '#15803d' },
}

/** AGE cell: day count only. The Overdue/Due-soon flag was removed — issues aren't deadline-tracked,
 *  only surveys are. To restore: re-add the `st: string` param, the `AlertTriangle` import, and the `st`
 *  computation in IssuesListRow, then uncomment the flag below. */
function AgeCell({ age }: { age: number }) {
  return (
    <span className="text-right">
      <span className="block text-[12px] font-bold tabular-nums text-gis-ink-2">{age}d</span>
      {/* {st === 'overdue' ? (
        <span className="mt-[2px] flex items-center justify-end gap-[3px] text-[10px] font-bold tracking-[.03em] text-gis-red uppercase">
          <AlertTriangle className="h-[10px] w-[10px] stroke-[2.4]" /> Overdue
        </span>
      ) : st === 'due-soon' ? (
        <span className="mt-[2px] flex items-center justify-end gap-[3px] text-[10px] font-bold tracking-[.03em] text-[#b45309] uppercase">
          <AlertTriangle className="h-[10px] w-[10px] stroke-[2.4]" /> Due soon
        </span>
      ) : null} */}
    </span>
  )
}

/** ISSUE cell: severity token + title and `{ref} · {department}` sub-line (ref omitted when null). */
function IssueCell({ issue, isSelected, onSelect }: { issue: GisIssue; isSelected: boolean; onSelect: () => void }) {
  const ref = issue.issueRef ? `${issue.issueRef} · ` : ''
  return (
    <button type="button" onClick={onSelect} aria-current={isSelected} className="flex min-w-0 items-center gap-[11px] text-left focus-visible:ring-2 focus-visible:ring-blue-500/60">
      <span className="grid h-[24px] w-[24px] flex-none place-items-center rounded-[8px] text-[11.5px] font-bold text-white" style={{ background: SEV_COLOR[issue.severity] }}>
        {SEV_LETTER[issue.severity]}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[12.5px] font-semibold text-gis-ink">{issue.title}</span>
        <span className="mt-[2px] block truncate text-[10.5px] font-medium text-gis-soft">{ref}{issue.department ?? '—'}</span>
      </span>
    </button>
  )
}

/** LOCATION cell (≥760px/list): site over mandal — the "where" without opening the detail. */
function LocationCell({ site, mandal }: { site: string | null; mandal: string | null }) {
  return (
    <span className={`${COL_LOCATION} min-w-0`}>
      <span className="block truncate text-[11.5px] font-medium text-gis-ink-2">{site ?? '—'}</span>
      {mandal && <span className="mt-[2px] block truncate text-[10.5px] text-gis-soft">{prettyName(mandal)}</span>}
    </span>
  )
}

/** STATUS cell (≥560px/list): semantic-tinted chip — surfaces the Pending sub-state the merged
 *  "Open" status view folds away. */
function StatusCell({ issue }: { issue: GisIssue }) {
  const label = shortStatus(issue)
  const c = STATUS_CHIP[label]
  return (
    <span className={COL_STATUS}>
      <span
        className="inline-flex items-center rounded-[6px] px-2 py-[3px] text-[10.5px] font-bold tracking-[.02em]"
        style={{ background: c.bg, color: c.text }}
      >
        {label}
      </span>
    </span>
  )
}

/** ACTION cell: the drill-in chevron that opens the issue in the right-side detail drawer. */
function ActionCell({ isSelected, onSelect }: { isSelected: boolean; onSelect: () => void }) {
  return (
    <span className="flex items-center justify-end gap-1">
      <button type="button" onClick={onSelect} aria-current={isSelected} aria-label="Open issue detail"
        className={`grid h-[26px] w-[26px] flex-none place-items-center rounded-[8px] transition hover:bg-gis-track focus-visible:ring-2 focus-visible:ring-blue-500/60 ${isSelected ? 'text-gis-navy' : 'text-gis-soft'}`}>
        <ChevronRight className="h-[16px] w-[16px]" />
      </button>
    </span>
  )
}

export interface IssuesListRowProps {
  issue: GisIssue
  mandalName: string | null
  targets: ResolutionTargets
  isSelected: boolean
  onSelect: () => void
  onLocate: (() => void) | null
  onHover: (id: string | null) => void
}

export function IssuesListRow({ issue, mandalName, isSelected, onSelect, onHover }: IssuesListRowProps) {
  const age = ageDays(issue.createdAt)
  return (
    <div
      data-issue-row={issue.id}
      onMouseEnter={() => onHover(issue.siteId)}
      onMouseLeave={() => onHover(null)}
      // Whole-row click target — the inner Issue/chevron buttons stay for keyboard focus; the guard
      // stops a bubbled inner-button click from pushing a second detail frame onto the drill stack.
      onClick={(e) => {
        if (e.target instanceof HTMLElement && e.target.closest('button')) {
          return
        }
        onSelect()
      }}
      className={`${ISSUE_TABLE_COLS} scroll-mt-2 cursor-pointer rounded-[12px] border px-3 py-[9px] transition-colors duration-[120ms] motion-reduce:transition-none ${isSelected ? 'border-gis-navy bg-[#eef3fb] ring-1 ring-gis-navy/20' : 'border-transparent hover:border-gis-line hover:bg-[#f7f9fc]'}`}
    >
      <IssueCell issue={issue} isSelected={isSelected} onSelect={onSelect} />

      <LocationCell site={issue.siteName} mandal={mandalName} />

      <StatusCell issue={issue} />

      <AgeCell age={age} />

      <ActionCell isSelected={isSelected} onSelect={onSelect} />
    </div>
  )
}
